import { FeatureFlags } from '@dupe/common/constants'
import * as Sentry from '@sentry/nextjs'
import axios from 'axios'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import useIsFeatureOn from '~/src/hooks/useIsFeatureOn'
import { createSelectors } from '~/src/lib/createSelectors'
import logger from '~/src/lib/logger'
import { UpdateUserPayload } from '~/src/types'

export enum OnboardingStep {
  WELCOME = 'WELCOME',
  SHOPPING_INTENT = 'SHOPPING_INTENT',
  QUALITY_VS_VALUE = 'QUALITY_VS_VALUE',
  DUPE_VALUE_PROP_ONE = 'DUPE_VALUE_PROP_ONE',
  DUPE_VALUE_PROP_TWO = 'DUPE_VALUE_PROP_TWO',
  JOB_TO_BE_DONE = 'JOB_TO_BE_DONE',
  INVITE_CODE = 'INVITE_CODE',
  PAYWALL = 'PAYWALL',
  COMPLETE = 'COMPLETE',
}

// Our frontend version of onboarding data
export interface OnboardingData {
  subscription?: {
    plan: string
    billingPeriod: string
    paymentStatus?: string
    liveMode?: boolean
  }
  stripeCustomerId?: string
  stripeSubscriptionId?: string
  // Onboarding data that will be saved to the database
  onboarding_data?: {
    shoppingIntent?: string | null
    qualityVsValue?: number | null
    jobToBeDone?: string | null
    isProfessional?: string | null
  }
}

interface OnboardingState {
  currentStep: OnboardingStep
  steps: OnboardingStep[]
  data: OnboardingData
  completedSteps: OnboardingStep[]

  // Actions
  setCurrentStep: (step: OnboardingStep) => void
  nextStep: (nextStep?: OnboardingStep) => void
  previousStep: () => void
  setStepData: (data: Partial<OnboardingData>) => void
  markStepComplete: (step: OnboardingStep) => void
  isStepComplete: (step: OnboardingStep) => boolean
  resetOnboarding: () => void
  setPaymentStatus: (status: 'paid' | 'failed' | 'cancel') => void

  // API Actions
  subscribeNewsletter: (params: {
    authUser: {
      uid: string
      accessToken?: string
      getIdToken?: () => Promise<string>
    }
    tempUserId?: string
    email: string
    firstName?: string
    refetchUserData: () => Promise<any>
  }) => Promise<{
    status: boolean
    error?: string
  }>
  addTagToBeehiiv: (params: {
    authUser: {
      uid: string
      accessToken?: string
      getIdToken?: () => Promise<string>
    }
    tag: string
    subscriptionId: string
    refetchUserData: () => Promise<any>
  }) => Promise<{
    status: boolean
    error?: string
  }>

  // New onboarding data actions
  setShoppingIntent: (intent: string) => void
  setQualityVsValue: (value: number) => void
  setJobToBeDone: (job: string) => void
  setIsProfessional: (isProfessional: string) => void
}

const INITIAL_DATA: OnboardingData = {
  subscription: {
    plan: 'free',
    billingPeriod: 'yearly',
  },
  onboarding_data: {
    shoppingIntent: null,
    qualityVsValue: null,
    jobToBeDone: null,
    isProfessional: null,
  },
}

const INITIAL_STATE: Omit<
  OnboardingState,
  | 'setCurrentStep'
  | 'nextStep'
  | 'previousStep'
  | 'setStepData'
  | 'markStepComplete'
  | 'isStepComplete'
  | 'resetOnboarding'
  | 'setProcessing'
  | 'setPaymentStatus'
  | 'subscribeNewsletter'
  | 'setShoppingIntent'
  | 'setQualityVsValue'
  | 'setJobToBeDone'
  | 'setIsProfessional'
  | 'addTagToBeehiiv'
> = {
  currentStep: OnboardingStep.WELCOME,
  steps: [
    OnboardingStep.WELCOME,
    OnboardingStep.INVITE_CODE,
    OnboardingStep.SHOPPING_INTENT,
    OnboardingStep.QUALITY_VS_VALUE,
    OnboardingStep.DUPE_VALUE_PROP_ONE,
    OnboardingStep.JOB_TO_BE_DONE,
    OnboardingStep.DUPE_VALUE_PROP_TWO,
    OnboardingStep.PAYWALL,
    OnboardingStep.COMPLETE,
  ],
  data: INITIAL_DATA,
  completedSteps: [],
}

// Create a single store instance with initialization check
const initializeStore = (preloadedState: Partial<OnboardingState> = {}) =>
  create<OnboardingState>()(
    persist(
      (set, get) => ({
        ...INITIAL_STATE,
        ...preloadedState,
        setCurrentStep: (step) => set({ currentStep: step }),
        nextStep: (nextStep?: OnboardingStep) => {
          const { currentStep, steps } = get()

          if (nextStep) {
            set({ currentStep: nextStep })
            return
          }

          const currentIndex = steps.indexOf(currentStep)
          if (currentIndex < steps.length - 1) {
            set({ currentStep: steps[currentIndex + 1] })
          }
        },
        previousStep: () => {
          const { currentStep, steps } = get()
          const currentIndex = steps.indexOf(currentStep)
          if (currentIndex > 0) {
            set({ currentStep: steps[currentIndex - 1] })
          }
        },
        setStepData: (data) =>
          set((state) => ({
            data: {
              ...state.data,
              ...data,
              // Ensure subscription is properly merged
              subscription: {
                ...(state.data.subscription || {}),
                ...(data.subscription || {}),
                // Ensure billing period is always defined
                billingPeriod:
                  data.subscription?.billingPeriod ||
                  state.data.subscription?.billingPeriod ||
                  'yearly',
                // Ensure plan is always defined
                plan:
                  data.subscription?.plan ||
                  state.data.subscription?.plan ||
                  'free',
              },
            },
          })),
        markStepComplete: (step) =>
          set((state) => ({
            ...state,
            completedSteps: [...new Set([...state.completedSteps, step])],
          })),
        isStepComplete: (step) => {
          const { completedSteps } = get()
          return completedSteps.includes(step)
        },
        resetOnboarding: () =>
          set({
            currentStep: OnboardingStep.WELCOME,
            data: INITIAL_DATA,
            completedSteps: [],
          }),
        setPaymentStatus: (status) =>
          set((state) => ({
            ...state,
            data: {
              ...state.data,
              subscription: {
                ...(state.data.subscription || {}),
                plan: state.data.subscription?.plan || 'free',
                billingPeriod:
                  state.data.subscription?.billingPeriod || 'yearly',
                paymentStatus: status,
              },
            },
          })),

        // New onboarding data actions
        setShoppingIntent: (intent: string) =>
          set((state) => ({
            data: {
              ...state.data,
              onboarding_data: {
                ...(state.data.onboarding_data || {}),
                shoppingIntent: intent,
              },
            },
          })),
        setQualityVsValue: (value: number) =>
          set((state) => ({
            data: {
              ...state.data,
              onboarding_data: {
                ...(state.data.onboarding_data || {}),
                qualityVsValue: value,
              },
            },
          })),
        setJobToBeDone: (job: string) =>
          set((state) => ({
            data: {
              ...state.data,
              onboarding_data: {
                ...(state.data.onboarding_data || {}),
                jobToBeDone: job,
              },
            },
          })),
        setIsProfessional: (isProfessional: string) =>
          set((state) => ({
            data: {
              ...state.data,
              onboarding_data: {
                ...(state.data.onboarding_data || {}),
                isProfessional: isProfessional,
              },
            },
          })),

        // Existing API Actions
        subscribeNewsletter: async ({
          authUser,
          tempUserId,
          email,
          firstName,
          refetchUserData,
        }) => {
          const valuesToStore = {
            tempUserIds: [tempUserId],
            email,
            firstName,
          } as UpdateUserPayload

          try {
            const token =
              authUser?.accessToken || (await authUser?.getIdToken?.())
            const res = await axios('/api/secure/newsletter-subscribe', {
              method: 'POST',
              data: valuesToStore,
              headers: {
                'x-authorization': token,
              },
            })

            if (res?.status !== 200) {
              return { status: false, error: 'Error subscribing to newsletter' }
            }

            await refetchUserData()
            return { status: true }
          } catch (error) {
            logger.error('[subscribeNewsletter]', { error })
            return { status: false, error: 'Error subscribing to newsletter' }
          }
        },
        addTagToBeehiiv: async ({
          authUser,
          tag,
          subscriptionId,
          refetchUserData,
        }) => {
          try {
            const token =
              authUser?.accessToken || (await authUser?.getIdToken?.())
            const res = await axios('/api/secure/newsletter-tag', {
              method: 'POST',
              data: {
                tag,
                subscriptionId,
              },
              headers: {
                'x-authorization': token,
              },
            })

            if (res?.status !== 200) {
              return { status: false, error: 'Error adding tag to subscriber' }
            }

            await refetchUserData()

            return { status: true }
          } catch (error) {
            logger.error('[addTagToBeehiiv]', { error })
            Sentry.captureException(error)
            return { status: false, error: 'Error adding tag to subscriber' }
          }
        },
      }),
      {
        name: 'onboarding-storage',
        storage: {
          getItem: (name) => {
            if (typeof window === 'undefined') return null

            const str = localStorage.getItem(name)
            if (!str) return null

            try {
              const { state } = JSON.parse(str)
              return {
                state: {
                  ...INITIAL_STATE,
                  ...state,
                  // Ensure data structure
                  data: {
                    ...INITIAL_DATA,
                    ...state.data,
                  },
                },
              }
            } catch (error) {
              return null
            }
          },
          setItem: (name, value) => {
            if (typeof window === 'undefined') return

            try {
              localStorage.setItem(name, JSON.stringify(value))
            } catch (error) {
              console.error('Error saving onboardingStore:', error)
            }
          },
          removeItem: (name) => {
            if (typeof window === 'undefined') return
            localStorage.removeItem(name)
          },
        },
      }
    )
  )

// Create a single store instance with initialization check
let store: ReturnType<typeof initializeStore> | undefined

const getStore = () => {
  if (typeof window === 'undefined') {
    // Always return a new store instance during SSR
    return initializeStore()
  }

  // Initialize store only once in client
  if (!store) {
    store = initializeStore()
  }
  return store
}

// Export the store hooks with selectors
export const useOnboardingStore = createSelectors(getStore())

// Specialized selector hooks
export const useOnboardingStep = () => {
  const currentStep = useOnboardingStore((state) => state.currentStep)
  const { featureOn: onboardingPaywallStepEnabled } = useIsFeatureOn(
    FeatureFlags.OnboardingPaywallStep
  )

  // Return COMPLETE if on paywall step but feature is disabled
  if (currentStep === OnboardingStep.PAYWALL && !onboardingPaywallStepEnabled) {
    return OnboardingStep.COMPLETE
  }

  return currentStep
}

export const useCompletedSteps = () =>
  useOnboardingStore((state) => state.completedSteps)

export const useOnboardingData = () =>
  useOnboardingStore((state) => state.data.onboarding_data)

export const useSubscriptionPlan = () =>
  useOnboardingStore((state) => state.data.subscription?.plan)
export const usePaymentStatus = () =>
  useOnboardingStore((state) => state.data.subscription?.paymentStatus)
export const useBillingPeriod = () =>
  useOnboardingStore((state) => state.data.subscription?.billingPeriod)

// Action groups for common operations
export const useOnboardingActions = () => {
  const store = useOnboardingStore()
  const { featureOn: onboardingPaywallStepEnabled } = useIsFeatureOn(
    FeatureFlags.OnboardingPaywallStep
  )

  return {
    nextStep: store.nextStep,
    previousStep: () => {
      const currentStep = store.currentStep
      const steps = store.steps
      const currentIndex = steps.indexOf(currentStep)

      if (currentIndex > 0) {
        const previousStep = steps[currentIndex - 1]
        const prePreviousStep = steps[currentIndex - 2]

        // If previous step would be paywall and feature is disabled, skip it
        if (
          previousStep === OnboardingStep.PAYWALL &&
          !onboardingPaywallStepEnabled
        ) {
          const existsInEnum =
            prePreviousStep && Object.values(OnboardingStep).includes(prePreviousStep)
          if (existsInEnum) {
            store.setCurrentStep(prePreviousStep)
          } else {
            store.setCurrentStep(OnboardingStep.WELCOME)
          }
          return
        }

        if (previousStep) {
          store.setCurrentStep(previousStep)
        }
      }
    },
    setStepData: store.setStepData,
    markStepComplete: store.markStepComplete,
    resetOnboarding: store.resetOnboarding,
    subscribeNewsletter: store.subscribeNewsletter,
    setPaymentStatus: store.setPaymentStatus,
    setCurrentStep: store.setCurrentStep,
    setShoppingIntent: store.setShoppingIntent,
    setQualityVsValue: store.setQualityVsValue,
    setJobToBeDone: store.setJobToBeDone,
    setIsProfessional: store.setIsProfessional,
    addTagToBeehiiv: store.addTagToBeehiiv,
  }
}
